// themeVariables.js

import { hexToRgba } from '../utilities/Colors.js';
import {
  THEME_COLOR_TOKEN_IDS,
  cssVarNameForToken,
} from './theme/themeTokenSchema.js';

/**
 * buildThemeRecipeCssVariables
 *
 * Derived CSS outputs (recipes). Honest names — not confused with `panel`.
 * See THEME_RECIPES in themeTokenSchema.js.
 *
 * @param {object} theme - Resolved theme token map.
 * @returns {Record<string, string>}
 */
function buildThemeRecipeCssVariables(theme) {
  const highlight = theme.highlight;

  return {
    [cssVarNameForToken('glass')]: hexToRgba(theme.background, 0.91),
    [cssVarNameForToken('glassSoft')]: hexToRgba(theme.background, 0.8),
    [cssVarNameForToken('glassBlur')]: '24px',
    [cssVarNameForToken('elevationShadow')]: `0 24px 80px ${hexToRgba(theme.foreground, 0.12)}`,
    [cssVarNameForToken('elevationShadowSoft')]: hexToRgba(theme.foreground, 0.12),
    [cssVarNameForToken('focusRing')]: `0 0 0 6px ${hexToRgba(highlight, 0.14)}`,
    [cssVarNameForToken('focusRingColor')]: hexToRgba(highlight, 0.14),
    [cssVarNameForToken('heroGlow')]: `radial-gradient(circle, ${hexToRgba(highlight, 0.14)} 0%, transparent 72%)`,
    [cssVarNameForToken('heroBackground')]: `radial-gradient(circle at top, ${hexToRgba(highlight, 0.12)} 0%, ${theme.background} 52%, ${theme.background} 100%)`,
  };
}

/**
 * buildThemeCssVariables
 *
 * Publishes canonical `--{tokenId}` for each color token, plus recipe/derived vars.
 *
 * @param {object|null|undefined} theme - Active resolved theme object.
 * @returns {Record<string, string>} CSS variable map.
 */
export function buildThemeCssVariables(theme) {
  if (!theme) {
    return {};
  }

  const variables = {};

  THEME_COLOR_TOKEN_IDS.forEach((tokenId) => {
    const value = theme[tokenId];
    if (value === undefined || value === null || value === '') {
      return;
    }
    variables[cssVarNameForToken(tokenId)] = value;
  });

  // highlightHover falls back to highlight when unset.
  if (!variables['--highlightHover'] && theme.highlight) {
    variables['--highlightHover'] = theme.highlight;
  }
  if (!variables['--highlightGlow'] && theme.highlight) {
    variables['--highlightGlow'] = hexToRgba(theme.highlight, 0.28);
  }

  Object.assign(variables, buildThemeRecipeCssVariables(theme));

  return variables;
}

/**
 * applyThemeCssVariables
 *
 * Writes theme CSS variables onto a CSSStyleDeclaration target.
 *
 * @param {CSSStyleDeclaration|null|undefined} target - Style target such as documentElement.style.
 * @param {Record<string, string>} variables - CSS variable map.
 */
export function applyThemeCssVariables(target, variables) {
  if (!target || !variables) {
    return;
  }

  Object.entries(variables).forEach(([name, value]) => {
    target.setProperty(name, value);
  });
}

/**
 * clearThemeCssVariables
 *
 * Removes theme CSS variables from a CSSStyleDeclaration target.
 *
 * @param {CSSStyleDeclaration|null|undefined} target - Style target such as documentElement.style.
 * @param {string[]} variableNames - CSS variable names to remove.
 */
export function clearThemeCssVariables(target, variableNames) {
  if (!target || !Array.isArray(variableNames)) {
    return;
  }

  variableNames.forEach((name) => {
    target.removeProperty(name);
  });
}
